import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { BookActivityView } from '@/types/api';

/**
 * Per-book activity PDFs. List is open to any authenticated reader (the
 * paywall lives in the UI); mutations are admin-only on the server.
 *
 * The create call is multipart — it carries a `title` text field and a
 * `file` part in the same request. The server collects both with
 * `req.parts()` so the order doesn't matter, but we follow `title` →
 * `file` here because that's the order most browsers send when iterating
 * a `FormData` instance.
 */
export const bookActivitiesService = {
  list(bookId: string) {
    return apiRequest<BookActivityView[]>(apiRoutes.books.activities(bookId));
  },

  /**
   * Admin-only. Creates a new activity for the given book. The server
   * persists the PDF to disk first, then inserts the row; on DB-insert
   * failure it best-effort unlinks the orphan file.
   */
  create(bookId: string, title: string, file: File) {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('file', file, file.name);
    return apiRequest<BookActivityView>(apiRoutes.books.activities(bookId), {
      method: 'POST',
      body: formData,
      raw: true, // let fetch set the multipart boundary header for us
    });
  },

  /**
   * Admin-only. Renames an activity. The PDF itself is immutable; to
   * replace the file the admin deletes the row and uploads a new one.
   */
  updateTitle(activityId: string, title: string) {
    return apiRequest<BookActivityView>(apiRoutes.activities.one(activityId), {
      method: 'PATCH',
      body: { title },
    });
  },

  /**
   * Admin-only. Deletes the row and unlinks the file on disk. Server
   * returns 204; the wrapper resolves to `undefined`.
   */
  remove(activityId: string) {
    return apiRequest<void>(apiRoutes.activities.one(activityId), {
      method: 'DELETE',
    });
  },
};
